/**
 * BuildingForm - Form for editing building parameters
 *
 * Displays form fields for the Single Unit Community template.
 * Updates preview in real-time as values change.
 * Supports multiple doors per wall.
 */

import { useForm, Controller, type Resolver } from 'react-hook-form'
import { zodResolver } from '@hookform/resolvers/zod'
import { useBuildingEditorStore, type SingleUnitFormValues, type DoorsConfig } from '@/stores/buildingEditorStore'
import { singleUnitFormSchema } from '@/lib/templates/single-unit-community'
import { Form } from '@/components/ui/form'
import { Button } from '@/components/ui/button'
import { Input } from '@/components/ui/input'
import { Label } from '@/components/ui/label'
import { Separator } from '@/components/ui/separator'
import { ScrollArea } from '@/components/ui/scroll-area'
import { DoorListEditor } from './shared/DoorListEditor'
import { PricingFeaturesSection } from './forms/PricingFeaturesSection'
import { pricingFieldsSchema, defaultPricingValues, type PricingFormFields } from '@/lib/schemas/pricing-fields'
import { useEffect } from 'react'
import { Loader2 } from 'lucide-react'
import { RemoveAreaButton } from './shared/RemoveAreaButton'

interface BuildingFormProps {
  onSave: (values: SingleUnitFormValues, pricing?: PricingFormFields) => void
  onCancel: () => void
  isCreating?: boolean
  isSaving?: boolean
  isPlaced?: boolean  // When false, building follows cursor - disable save until placed
  /** Area ID for delete functionality (edit mode only) */
  areaId?: string
  /** Area name for delete confirmation message */
  areaName?: string
  /** Called after area is successfully removed */
  onRemoved?: () => void
  /** Initial pricing values (for edit mode) */
  initialPricing?: Partial<PricingFormFields>
}

export function BuildingForm({ onSave, onCancel, isCreating = true, isSaving = false, isPlaced = true, areaId, areaName, onRemoved, initialPricing }: BuildingFormProps) {
  const { formValues, updateFormValues, previewBuilding } = useBuildingEditorStore()

  const form = useForm<SingleUnitFormValues>({
    // zodResolver types its result as Resolver<z.input, any, z.output>; schema
    // `.default()`s make those differ, so it will not unify with the form's
    // output-typed values under react-hook-form v7.67. Defaults are applied at
    // parse time, so the cast is sound.
    resolver: zodResolver(singleUnitFormSchema) as unknown as Resolver<SingleUnitFormValues>,
    defaultValues: formValues || undefined,
  })

  const pricingForm = useForm<PricingFormFields>({
    // zodResolver types its result as Resolver<z.input, any, z.output>; schema
    // `.default()`s make those differ, so it will not unify with the form's
    // output-typed values under react-hook-form v7.67. Defaults are applied at
    // parse time, so the cast is sound.
    resolver: zodResolver(pricingFieldsSchema) as unknown as Resolver<PricingFormFields>,
    defaultValues: { ...defaultPricingValues, ...initialPricing },
  })

  const { register, handleSubmit, watch, setValue, control, formState: { errors } } = form

  // Watch all values to update preview
  const watchedValues = watch()

  // Sync bearing_deg from store when it changes externally (e.g., from mouse rotation)
  // This prevents the form from overwriting store updates with stale form values
  useEffect(() => {
    if (formValues && watchedValues.bearing_deg !== formValues.bearing_deg) {
      // Store has different bearing - sync form to store (external update like mouse rotation)
      setValue('bearing_deg', formValues.bearing_deg)
    }
  }, [formValues?.bearing_deg, setValue])

  // Update preview building when form values change (from user input in form)
  // CRITICAL: Only sync form->store, not store->form (that's handled above)
  useEffect(() => {
    if (formValues) {
      // Check if form values differ from store (excluding bearing which syncs from store)
      const hasFormChanges = Object.keys(watchedValues).some((key) => {
        // Skip bearing_deg - it's synced from store to form, not form to store
        if (key === 'bearing_deg') return false
        const formVal = watchedValues[key as keyof SingleUnitFormValues]
        const storeVal = formValues[key as keyof SingleUnitFormValues]
        // Deep compare for objects (like doors)
        if (typeof formVal === 'object' && typeof storeVal === 'object') {
          return JSON.stringify(formVal) !== JSON.stringify(storeVal)
        }
        return formVal !== storeVal
      })
      if (hasFormChanges) {
        // Preserve the store's bearing_deg when updating from form
        updateFormValues({ ...watchedValues, bearing_deg: formValues.bearing_deg })
      }
    }
  }, [watchedValues, formValues, updateFormValues])

  // Show position from preview building
  const latitude = previewBuilding?.latitude?.toFixed(6) || '—'
  const longitude = previewBuilding?.longitude?.toFixed(6) || '—'

  const onSubmit = async (values: SingleUnitFormValues) => {
    // Validate pricing form
    const pricingValid = await pricingForm.trigger()
    if (!pricingValid) return

    // Include position from preview building
    if (previewBuilding) {
      values.latitude = previewBuilding.latitude
      values.longitude = previewBuilding.longitude
    }
    onSave(values, pricingForm.getValues())
  }

  return (
    <form onSubmit={handleSubmit(onSubmit)} className="flex flex-col h-full flex-1 min-h-0">
      {/* Scrollable form content */}
      <ScrollArea className="flex-1 min-h-0">
        <div className="p-4 space-y-6">
          {/* Building Section Header */}
          <div className="bg-blue-50 dark:bg-blue-950/30 -mx-4 px-4 py-2 border-y">
            <h3 className="font-semibold text-sm text-blue-700 dark:text-blue-300">Building (Envelope)</h3>
            <p className="text-xs text-muted-foreground">Physical structure with walls and roof</p>
          </div>

          {/* Identity Section */}
          <div className="space-y-3">
            <h4 className="font-medium text-sm text-muted-foreground">Identity</h4>

            <div className="space-y-2">
              <Label htmlFor="name">Building Name</Label>
              <Input
                id="name"
                {...register('name')}
                placeholder="e.g., Main Hangar"
              />
              {errors.name && (
                <p className="text-xs text-destructive">{errors.name.message}</p>
              )}
            </div>

            <div className="space-y-2">
              <div className="flex items-center gap-2">
                <Label htmlFor="code">Building Code</Label>
                <span className="text-xs text-muted-foreground">(auto-generated)</span>
              </div>
              <Input
                id="code"
                {...register('code')}
                placeholder="e.g., COM-001"
              />
              {errors.code && (
                <p className="text-xs text-destructive">{errors.code.message}</p>
              )}
            </div>
          </div>

          <Separator />

          {/* Dimensions Section */}
          <div className="space-y-3">
            <h4 className="font-medium text-sm text-muted-foreground">Dimensions (feet)</h4>

            <div className="grid grid-cols-3 gap-2">
              <div className="space-y-2">
                <Label htmlFor="width_ft">Width</Label>
                <Input
                  id="width_ft"
                  type="number"
                  {...register('width_ft', { valueAsNumber: true })}
                />
                {errors.width_ft && (
                  <p className="text-xs text-destructive">{errors.width_ft.message}</p>
                )}
              </div>

              <div className="space-y-2">
                <Label htmlFor="depth_ft">Depth</Label>
                <Input
                  id="depth_ft"
                  type="number"
                  {...register('depth_ft', { valueAsNumber: true })}
                />
                {errors.depth_ft && (
                  <p className="text-xs text-destructive">{errors.depth_ft.message}</p>
                )}
              </div>

              <div className="space-y-2">
                <Label htmlFor="height_ft">Height</Label>
                <Input
                  id="height_ft"
                  type="number"
                  {...register('height_ft', { valueAsNumber: true })}
                />
                {errors.height_ft && (
                  <p className="text-xs text-destructive">{errors.height_ft.message}</p>
                )}
              </div>
            </div>
          </div>

          <Separator />

          {/* Unit Section Header */}
          <div className="bg-green-50 dark:bg-green-950/30 -mx-4 px-4 py-2 border-y">
            <h3 className="font-semibold text-sm text-green-700 dark:text-green-300">Unit (Leasable Space)</h3>
            <p className="text-xs text-muted-foreground">Interior space and door specifications</p>
          </div>

          {/* Door Configuration Section - Multi-door support */}
          <Controller
            name="doors"
            control={control}
            render={({ field }) => (
              <DoorListEditor
                doors={field.value as DoorsConfig}
                onChange={field.onChange}
                widthFt={watchedValues.width_ft || 100}
                depthFt={watchedValues.depth_ft || 80}
                heightFt={watchedValues.height_ft || 24}
              />
            )}
          />

          <Separator />

          {/* Appearance Section */}
          <div className="space-y-3">
            <h4 className="font-medium text-sm text-muted-foreground">Appearance</h4>

            <div className="grid grid-cols-2 gap-2">
              <div className="space-y-2">
                <Label htmlFor="floor_color">Floor Color</Label>
                <div className="flex gap-2">
                  <Input
                    id="floor_color_picker"
                    type="color"
                    value={watchedValues.floor_color || '#808080'}
                    onChange={(e) => setValue('floor_color', e.target.value)}
                    className="w-12 h-9 p-1 cursor-pointer"
                  />
                  <Input
                    id="floor_color"
                    {...register('floor_color')}
                    placeholder="#808080"
                    className="flex-1"
                  />
                </div>
              </div>

              <div className="space-y-2">
                <Label htmlFor="wall_color">Wall Color</Label>
                <div className="flex gap-2">
                  <Input
                    id="wall_color_picker"
                    type="color"
                    value={watchedValues.wall_color || '#A0A0A0'}
                    onChange={(e) => setValue('wall_color', e.target.value)}
                    className="w-12 h-9 p-1 cursor-pointer"
                  />
                  <Input
                    id="wall_color"
                    {...register('wall_color')}
                    placeholder="#A0A0A0"
                    className="flex-1"
                  />
                </div>
              </div>
            </div>
          </div>

          <Separator />

          {/* Position Section */}
          <div className="space-y-3">
            <h4 className="font-medium text-sm text-muted-foreground">Position</h4>
            <p className="text-xs text-muted-foreground">Drag the building on the map to set position</p>

            <div className="grid grid-cols-2 gap-2">
              <div className="space-y-1">
                <Label className="text-xs">Latitude</Label>
                <div className="px-3 py-2 bg-muted rounded-md text-sm font-mono">{latitude}</div>
              </div>
              <div className="space-y-1">
                <Label className="text-xs">Longitude</Label>
                <div className="px-3 py-2 bg-muted rounded-md text-sm font-mono">{longitude}</div>
              </div>
            </div>

            <div className="space-y-2">
              <Label htmlFor="bearing_deg">Rotation (degrees)</Label>
              <div className="flex gap-2 items-center">
                <Input
                  id="bearing_deg"
                  type="number"
                  min={0}
                  max={360}
                  step="any"
                  {...register('bearing_deg', { valueAsNumber: true })}
                  className="flex-1"
                />
                <span className="text-sm text-muted-foreground">°</span>
              </div>
              {errors.bearing_deg && (
                <p className="text-xs text-destructive">{errors.bearing_deg.message}</p>
              )}
            </div>

            <div className="space-y-2">
              <Label htmlFor="elevation_ft">Elevation (ft)</Label>
              <Input
                id="elevation_ft"
                type="number"
                step={0.5}
                {...register('elevation_ft', { valueAsNumber: true })}
              />
              <p className="text-xs text-muted-foreground">
                Height above ground (negative values sink below ground)
              </p>
              {errors.elevation_ft && (
                <p className="text-xs text-destructive">{errors.elevation_ft.message}</p>
              )}
            </div>
          </div>

          <Separator />

          {/* Pricing & Building Features */}
          <Form {...pricingForm}>
            <PricingFeaturesSection form={pricingForm} />
          </Form>

          {/* Remove Area - only in edit mode */}
          {!isCreating && areaId && onRemoved && (
            <RemoveAreaButton
              areaId={areaId}
              areaName={areaName || 'this area'}
              onRemoved={onRemoved}
            />
          )}
        </div>
      </ScrollArea>

      {/* Sticky footer with actions */}
      <div className="border-t p-4 flex gap-2 shrink-0">
        <Button type="button" variant="outline" onClick={onCancel} className="flex-1" disabled={isSaving}>
          Cancel
        </Button>
        <Button type="submit" className="flex-1" disabled={isSaving || !isPlaced}>
          {isSaving ? (
            <>
              <Loader2 className="mr-2 h-4 w-4 animate-spin" />
              Saving...
            </>
          ) : (
            isCreating ? 'Create Building' : 'Save Changes'
          )}
        </Button>
      </div>
    </form>
  )
}
